const resources = [
  {
    title: "U.S. State Department — Passport Information",
    url: "https://travel.state.gov/content/travel/en/passports.html",
    description:
      "Official passport requirements, forms, processing times, and eligibility guidelines from the U.S. Department of State. This is the authoritative source for all passport rules.",
    icon: "ri-government-line",
  },
  {
    title: "U.S. State Department — Travel Advisories",
    url: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html",
    description:
      "Real-time travel advisories for every country worldwide. Essential for Venice travelers heading to the Caribbean, Europe, Canada, or anywhere abroad. Entry requirements change frequently.",
    icon: "ri-alert-line",
  },
  {
    title: "USPS Venice Passport Services",
    url: "https://tools.usps.com/find-location.htm",
    description:
      "Find the nearest USPS passport acceptance facility. Search by ZIP code 34285 or 34292 for Venice locations. Call ahead for appointments — walk-ins are rarely accepted.",
    icon: "ri-map-pin-line",
  },
  {
    title: "Sarasota-Bradenton International Airport (SRQ)",
    url: "https://flysrq.com/",
    description:
      "SRQ is Venice's primary commercial airport, just 30–40 minutes north via US-41. Check international flight schedules, customs requirements, and seasonal routes to Toronto, London, and Frankfurt.",
    icon: "ri-plane-line",
  },
  {
    title: "Sarasota County Government",
    url: "https://www.scgov.net/",
    description:
      "Official Sarasota County website with information on county clerk services, notarization, public records, and local government offices that support passport and travel document needs in Venice and Sarasota.",
    icon: "ri-building-line",
  },
  {
    title: "U.S. Customs and Border Protection — Real ID",
    url: "https://www.dhs.gov/real-id",
    description:
      "REAL ID requirements for domestic flights. As of May 2025, a REAL ID-compliant driver's license or passport is required for all domestic air travel. Venice residents should verify their Florida driver's license compliance before any trip.",
    icon: "ri-id-card-line",
  },
];

export default function VeniceHelpfulResourcesSection() {
  return (
    <section id="local-resources" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Helpful Links
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Venice Travelers
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Official links and tools every Venice, FL traveler should bookmark before planning international travel.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Sarasota County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Sarasota County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Sarasota County. Online, mail, or in-person options available.", url: "https://sarasota.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Sarasota County. Required for estate and legal matters.", url: "https://sarasota.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Sarasota County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.sarasotaclerk.com/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Sarasota County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.sarasotaclerk.com/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-600 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Sarasota County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {resources.map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0 group-hover:bg-[#F6C344] transition-colors">
                  <i className={`${r.icon} text-[#F6C344] group-hover:text-[#1A365D] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A365D] text-sm mb-1 group-hover:text-[#B22222] transition-colors">
                    {r.title}
                    <i className="ri-external-link-line ml-1.5 text-[10px] text-gray-400"></i>
                  </h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{r.description}</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}